import {useTranslation} from '@aboutyou/i18n';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import React, {useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {tabletAndDown} from '../../../constants/customMedia';
import {hideScrollBar, touchScroll} from '../../../constants/scrolling';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {
  ProgressBarContainer,
  SliderProgressBar,
} from '../../atoms/SliderProgressBar/SliderProgressBar';
import {useSlider} from '../../molecules/HorizontalSlider/hooks/useSlider';

interface CardsSliderProps<Card> {
  className?: string;
  cards: Card[];
  CardComponent: React.ComponentType<
    Card & {
      isCloseToViewport: boolean;
      ['data-testid']: string;
    }
  >;
  extendFirstCardTimeout?: boolean;
}

const EXTENDED_FIRST_CARD_TIMEOUT = 18;

export const CardsSlider = <Card extends {duration: {seconds: number}}>({
  className,
  cards,
  CardComponent,
  extendFirstCardTimeout,
}: CardsSliderProps<Card>) => {
  const {t} = useTranslation();
  const sliderRef = useRef<HTMLUListElement>(null);
  const {
    scrollToPrevItem,
    scrollToNextItem,
    currentSlide,
    slidesNumber,
    scrollToSlide,
  } = useSlider(sliderRef);
  const isInitialMount = !useHydrationDone();
  const [transitionSlideId, setTransitionSlideId] = useState<number>(-1);
  const longerFirstCardTimeout = currentSlide === 0 && extendFirstCardTimeout;
  useEffect(() => {
    if (currentSlide === transitionSlideId) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setTransitionSlideId(-1);
    }
  }, [currentSlide, transitionSlideId]);

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    const changeSlide = () => {
      const newIndex = currentSlide + 1;
      const canSlideNext = newIndex < cards.length;

      if (!canSlideNext) {
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setTransitionSlideId(0);
      }

      // eslint-disable-next-line local-rules/set-state-in-use-effect
      scrollToSlide(canSlideNext ? newIndex : 0);
    };

    const card = cards[currentSlide];

    let slideInterval: undefined | number;
    if (card) {
      if (longerFirstCardTimeout) {
        slideInterval = window.setInterval(
          changeSlide,
          EXTENDED_FIRST_CARD_TIMEOUT * 1000,
        );
      } else {
        slideInterval = window.setInterval(
          changeSlide,
          card.duration.seconds * 1000,
        );
      }
    }

    return () => {
      if (slideInterval) {
        window.clearInterval(slideInterval);
      }
    };
  }, [cards, currentSlide, scrollToSlide]);

  return (
    <SliderContainer className={className}>
      <SlidesContainer ref={sliderRef}>
        {cards.map((card, index) => {
          // On the initial mount we only want to prioritize the first image, as it's the highest priority on the server.
          // In the client we have enough time to prioritize the first two images to preload what the user will most probably unfold soon.
          // Logic and comment are taken from the product images on ADP.
          const isCloseToViewport = isInitialMount
            ? index === currentSlide
            : currentSlide - 1 <= index && currentSlide + 1 >= index;

          return (
            <CardComponent
              key={index}
              {...card}
              isCloseToViewport={isCloseToViewport}
              data-testid={`card-${index}${
                index === currentSlide ? '-active' : ''
              }`}
            />
          );
        })}
      </SlidesContainer>

      {cards.length > 1 && (
        <StyledProgressBarContainer>
          {cards.map((card, index) => {
            const nextActiveSlide =
              transitionSlideId >= 0 ? transitionSlideId : currentSlide;

            const cardDuration =
              extendFirstCardTimeout && index === 0
                ? EXTENDED_FIRST_CARD_TIMEOUT * 1000
                : card.duration.seconds * 1000;

            return (
              <SliderProgressBar
                key={index}
                durationInMs={cardDuration}
                active={nextActiveSlide === index}
                done={nextActiveSlide > index}
                datatestId={`progressBar-${index}`}
                onClick={() => {
                  setTransitionSlideId(index);
                  scrollToSlide(index);
                }}
              />
            );
          })}
        </StyledProgressBarContainer>
      )}

      <Controls>
        <ControlButton
          onClick={() => scrollToPrevItem()}
          disabled={currentSlide === 0}
          data-testid="previous-card-button"
          aria-label={t('Go to the previous slide')}
        >
          <StyledIcon asset={GenericIconName.ArrowLeft} />
        </ControlButton>
        <ControlButton
          onClick={() => scrollToNextItem()}
          disabled={currentSlide === slidesNumber - 1}
          data-testid="next-card-button"
          aria-label={t('Go to the next slide')}
        >
          <StyledIcon asset={GenericIconName.ArrowRight} />
        </ControlButton>
      </Controls>
    </SliderContainer>
  );
};

const FIXED_HEIGHT_MARGIN = 100;

export const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: calc(
    100vh - var(--layout-header-height, 0) - ${FIXED_HEIGHT_MARGIN}px
  );
  min-height: 470px;
  margin-left: auto;
  margin-right: auto;

  @media only screen and (max-height: 800px) {
    height: calc(100vh - var(--layout-header-height, 0));
  }
`;

const SlidesContainer = styled.ul`
  display: flex;
  flex-wrap: nowrap;
  overflow: auto hidden;
  scroll-snap-type: x mandatory;
  height: 100%;
  list-style: none;

  ${touchScroll};
  ${hideScrollBar};

  > * {
    min-width: 100%;
    scroll-snap-align: center;
    scroll-snap-stop: always;
  }
`;

const Controls = styled.div`
  position: absolute;
  bottom: 40px;
  right: 60px;
  display: flex;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0px 3px 12px 2px #00000012, 0px 2px 3px 0px #00000014;
  z-index: 2;

  ${tabletAndDown(css`
    display: none;
  `)}
`;

const ControlButton = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${COLORS.White};
  opacity: 0.6;
  width: 32px;
  height: 32px;
  cursor: unset;

  svg {
    opacity: 0.6;
  }

  &:hover:not(:disabled) {
    opacity: 1;
    cursor: pointer;

    svg {
      opacity: 1;
    }
  }
`;

const StyledIcon = styled(Icon)`
  width: 20px;
  height: 20px;
`;

const StyledProgressBarContainer = styled(ProgressBarContainer)`
  bottom: 28px;
  right: 0;
  margin: auto;
  position: absolute;
  left: 0;
  z-index: 1;
`;
